export const SLEEP_QUESTIONS = {
  safety: {
    id: 'safety',
    number: 1,
    text: 'First, let\'s confirm, do you currently have any of the following conditions?',
    hint: 'For your health and safety, we need to know these first',
    type: 'checkbox',
    options: [
      { value: 'pregnant', label: '🤰 Pregnant or breastfeeding' },
      { value: 'medication', label: '💊 Taking sleeping pills, anti-anxiety, or anti-depressant medication' },
      { value: 'disease', label: '❤️ Have severe heart, liver, or kidney disease' },
      { value: 'allergy', label: '🚫 Allergic to certain herbal medicines' },
      { value: 'none', label: '✓ None of the above, continue', exclusive: true }
    ]
  },
  demographics: {
    id: 'demographics',
    number: 2,
    text: 'Tell us some basic information',
    type: 'multi-part',
    parts: [
      {
        id: 'age',
        label: 'Age Group',
        type: 'radio',
        options: [
          { value: '18-35', label: '18-35 years' },
          { value: '36-50', label: '36-50 years' },
          { value: '51-65', label: '51-65 years' },
          { value: 'other', label: 'Other age group' }
        ]
      },
      {
        id: 'weight',
        label: 'Weight',
        type: 'radio',
        options: [
          { value: '<50', label: '< 110 lbs (< 50 kg)' },
          { value: '50-70', label: '110-154 lbs (50-70 kg)' },
          { value: '70-90', label: '155-200 lbs (70-90 kg)' },
          { value: '>90', label: '> 200 lbs (> 90 kg)' }
        ]
      }
    ]
  },
  mainIssue: {
    id: 'mainIssue',
    number: 3,
    text: 'What is your main sleep concern?',
    hint: 'You can select 1-2 that bother you the most',
    type: 'checkbox',
    maxSelect: 2,
    options: [
      { value: 'difficulty_falling', label: '😫 Difficulty falling asleep (takes a long time after lying down)' },
      { value: 'light_sleep', label: '😴 Light sleep, easily woken up' },
      { value: 'early_wake', label: '⏰ Early awakening, cannot fall back asleep' },
      { value: 'poor_quality', label: '😓 Poor quality, still tired after sleeping enough' }
    ]
  },
  timeToSleep: {
    id: 'timeToSleep',
    number: 4,
    text: 'How long does it usually take to fall asleep after lying down?',
    type: 'radio',
    condition: (answers) => answers.mainIssue?.includes('difficulty_falling'),
    options: [
      { value: '30-60min', label: '⏱️ 30 minutes to 1 hour' },
      { value: '1-2hr', label: '⏱️ 1-2 hours' },
      { value: '>2hr', label: '⏱️ Over 2 hours, sometimes sleepless all night' }
    ]
  },
  wakeTimes: {
    id: 'wakeTimes',
    number: 4,
    text: 'How many times do you usually wake up per night?',
    type: 'radio',
    condition: (answers) => answers.mainIssue?.includes('light_sleep') || answers.mainIssue?.includes('early_wake'),
    options: [
      { value: '1-2', label: '1-2 times, but can fall back asleep' },
      { value: '3-4', label: '3-4 times, difficult to fall back asleep' },
      { value: 'many', label: 'Many times, or cannot sleep after waking' }
    ]
  },
  fallingFeeling: {
    id: 'fallingFeeling',
    number: 5,
    text: 'When you can\'t sleep, which feeling is closest?',
    type: 'radio',
    condition: (answers) => answers.mainIssue?.includes('difficulty_falling'),
    options: [
      { value: 'racing_thoughts', label: '🧠 Mind racing, can\'t stop thinking' },
      { value: 'tired_cant_sleep', label: '😰 Body is tired, but just can\'t sleep' },
      { value: 'anxious', label: '💗 Anxious, restless, sometimes fast heartbeat' },
      { value: 'no_feeling', label: '😐 No specific discomfort, just can\'t sleep' }
    ]
  },
  wakeFeeling: {
    id: 'wakeFeeling',
    number: 5,
    text: 'When waking up in the middle of the night',
    type: 'radio',
    condition: (answers) => answers.mainIssue?.includes('light_sleep') || answers.mainIssue?.includes('early_wake'),
    options: [
      { value: 'racing_thoughts', label: '🧠 Mind suddenly alert, start thinking' },
      { value: 'anxious', label: '💗 Fast heartbeat, sweating, or feeling anxious' },
      { value: 'no_feeling', label: '😐 No specific feeling, just woke up' }
    ]
  },
  duration: {
    id: 'duration',
    number: 6,
    text: 'How long have you had these sleep issues?',
    type: 'radio',
    options: [
      { value: '<1month', label: '📅 Started within the last month' },
      { value: '1-3months', label: '📅 1-3 months' },
      { value: '>3months', label: '📅 Over 3 months (chronic insomnia)' }
    ]
  },
  frequency: {
    id: 'frequency',
    number: 7,
    text: 'Approximate frequency?',
    type: 'radio',
    options: [
      { value: '1-2', label: '1-2 times per week' },
      { value: '3-4', label: '3-4 times per week' },
      { value: '5+', label: 'Almost every day (5+ times per week)' }
    ]
  },
  daytimeImpact: {
    id: 'daytimeImpact',
    number: 8,
    text: 'Because of poor sleep, do you have these effects during the day?',
    hint: 'Select all that apply',
    type: 'checkbox',
    options: [
      { value: 'fatigue', label: '😴 Poor spirit, hard to concentrate' },
      { value: 'irritable', label: '😤 Easily irritable or anxious' },
      { value: 'tired', label: '🥱 Body feels exhausted' },
      { value: 'headache', label: '🤕 Dizziness or headache' },
      { value: 'none', label: '😊 It\'s okay, doesn\'t affect work and life much', exclusive: true }
    ]
  },
  constitution1: {
    id: 'constitution1',
    number: 9,
    text: 'Do you also tend to have:',
    type: 'checkbox',
    condition: (answers) => 
      answers.fallingFeeling === 'anxious' || answers.wakeFeeling === 'anxious',
    options: [
      { value: 'hot_hands', label: '🔥 Hot palms and soles, or night sweats' },
      { value: 'dry_mouth', label: '💧 Dry mouth, always want to drink water' },
      { value: 'anxious', label: '😰 Nervous or anxious' },
      { value: 'none', label: 'None of the above', exclusive: true }
    ]
  },
  constitution2: {
    id: 'constitution2',
    number: 9,
    text: 'Do you also have:',
    type: 'checkbox',
    condition: (answers) => answers.daytimeImpact?.includes('tired'),
    options: [
      { value: 'poor_digestion', label: '🍽️ Poor digestion, poor appetite' },
      { value: 'shortness_breath', label: '😮‍💨 Shortness of breath, tired climbing stairs' },
      { value: 'cold_limbs', label: '🧊 Hands and feet easily cold' },
      { value: 'none', label: 'None of the above', exclusive: true }
    ]
  }
};
